import { Input, Select, Space } from '@yimoka/antd';
import { observer, useField } from '@yimoka/react';
import { useCallback, useEffect } from 'react';

import { useTranslation } from '@/locales/i18n';

export interface CountryCodeOption {
  code: string;
  name: string;
  prefix: string;
}

// 登录页先保留线上已启用的中国区号；后续开放更多国家时只扩展这里的选项。
const COUNTRY_CODES: CountryCodeOption[] = [
  { code: 'CN', name: '中国', prefix: '+86' },
];

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  countryCode?: string;
  onCountryCodeChange?: (countryCode: string) => void;
  phoneNumber?: string;
  onPhoneNumberChange?: (phoneNumber: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  showCountrySelector?: boolean;
  countrySelectorWidth?: number;
  [key: string]: unknown;
}

function parsePhoneValue(fullPhone = '') {
  if (!fullPhone) return { code: '+86', number: '' };

  const matchedCountry = COUNTRY_CODES.find(country => fullPhone.startsWith(country.prefix));
  if (matchedCountry) {
    return {
      code: matchedCountry.prefix,
      number: fullPhone.substring(matchedCountry.prefix.length).replace(/^-/, ''),
    };
  }

  return { code: '+86', number: fullPhone.replace(/^\+86-?/, '') };
}

export const PhoneInput = observer((props: PhoneInputProps) => {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    countryCode,
    onCountryCodeChange,
    phoneNumber,
    onPhoneNumberChange,
    placeholder: placeholderProp,
    disabled = false,
    readOnly = false,
    showCountrySelector = true,
    countrySelectorWidth = 80,
    ...restProps
  } = props;
  const placeholder = placeholderProp ?? t('Account:login.phonePlaceholder');

  const field = useField();
  const currentPhone = parsePhoneValue(value);
  const currentCountryCode = countryCode || currentPhone.code;
  const currentPhoneNumber = phoneNumber || currentPhone.number;

  const updatePhonePrefix = useCallback((prefix: string) => {
    field?.form?.setFieldState('phonePrefix', (state) => {
      state.value = prefix.replace(/^\+/, '');
    });
  }, [field]);

  useEffect(() => {
    updatePhonePrefix(currentCountryCode);
  }, [currentCountryCode, updatePhonePrefix]);

  const handleCountryCodeChange = (newCountryCode: string | string[]) => {
    const code = Array.isArray(newCountryCode) ? newCountryCode[0] : newCountryCode;
    updatePhonePrefix(code);
    onCountryCodeChange?.(code);
    onChange?.(currentPhoneNumber || '');
  };

  const handlePhoneNumberChange = (newPhoneNumber: string) => {
    onPhoneNumberChange?.(newPhoneNumber);
    onChange?.(newPhoneNumber);
  };

  const countryOptions = COUNTRY_CODES.map(country => ({
    label: country.prefix,
    value: country.prefix,
    key: country.code,
  }));

  if (!showCountrySelector) {
    return (
      <Input
        {...restProps}
        disabled={disabled}
        placeholder={placeholder}
        readOnly={readOnly}
        type="tel"
        value={currentPhoneNumber}
        onChange={handlePhoneNumberChange}
      />
    );
  }

  return (
    <Space.Compact className="account-phone-input">
      <Select
        disabled={disabled}
        options={countryOptions}
        style={{ width: countrySelectorWidth, height: 54 }}
        value={currentCountryCode}
        variant="underlined"
        onChange={handleCountryCodeChange}
      />
      <Input
        {...restProps}
        disabled={disabled}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ flex: 1, height: 54 }}
        type="tel"
        value={currentPhoneNumber}
        variant="underlined"
        onChange={handlePhoneNumberChange}
      />
    </Space.Compact>
  );
});
